
import { Vue, Component, Prop, Emit, Watch, Ref } from 'vue-property-decorator'
import { ElForm } from 'element-ui/types/form'
import UploadImg from '@/common/class/UploadImg'
import { AppModule } from '@/store/modules/app'
import { convertCodeToText, convertTextToCode } from '@/common/area_data'
import { withApiHost } from '@/common/utils'

/**
 * @prop clueSortList   类别列表
 * @prop imgLimit       图片限制
 * @prop value          详情数据
 * @prop UploadRemove   移除图片回调
 * @prop ClearFiles     一键清空图片回调
 * @prop width          输入框宽度
 * @prop disabled       是否不可编辑
 * @Emit preview        查看图片大图
*/

@Component({
    components: {}
})
export default class Publish extends Vue {
    @Prop({ default: () => [] }) private clueSortList!: any
    @Prop({ default: 9 }) private imgLimit!: number
    @Prop({ default: '' }) private value!: any
    @Prop({ default: '' }) private UploadRemove!: any
    @Prop({ default: '' }) private ClearFiles!: any
    @Prop({ default: '50%' }) private width!: string
    @Prop({ default: false }) private disabled!: boolean
    @Ref('upload') private uploadRef!: any
    @Ref('ruleForm') private ruleForm!: ElForm

    private rawFileList: File[] = []
    private fileList: any[] = []
    private isInit = false
    private isChange = false
    private clue: any = { 
        clue_type_id: '',
        title: '',
        content: '',
        images: [],
        region: '',
        annexes: []
    }
    private clueRules = {
        clue_type_id: [
            { required: true, validator: this.typeValidator }
        ],
        title: [
            { required: true, validator: this.titleValidator }
        ],
        content: [
            { required: true, validator: this.contentValidator }
        ]
    }

    get uploadEl() {
        const selector = '.publish-wrapper .el-upload.el-upload--picture-card'
        return document.querySelector(selector) as HTMLElement
    }

    get provinceAndCityData() {
        return AppModule.regionData
    }

    /* ---------------------- 分界线 ---------------------- */

    @Watch('value')
    private async onValueChange(val: any) {
        if (!val) return
        // 如果没有地域代码
        if (!this.provinceAndCityData.length) {
            await AppModule.getRegion()
        }
        this.clue = JSON.parse(JSON.stringify(this.value))
        this.clue.region = convertTextToCode(this.clue.region)
        this.fileList = this.value.fileList || []
        this.isInit = true
        // annexes 契约：对象数组（新后端，与 Contact/Media 一致）；过渡期兼容老后端的 JSON 字符串
        const rawAnnexes: any = this.clue.annexes
        if (Array.isArray(rawAnnexes)) {
            this.clue.annexes = rawAnnexes
        } else {
            try {
                this.clue.annexes = rawAnnexes ? JSON.parse(rawAnnexes) : []
            } catch {
                this.clue.annexes = []
            }
        }
    }

    @Emit('preview')
    private onPreviewPhoto(file: any) {
        return file
    }

    private clear() {
        this.ruleForm.resetFields()
    }

    private typeValidator(rule: any, value: string, callback: Function) {
        if (this.isInit) return callback()
        let message = ''
        if (value == null) {
            message = '请选择类别'
        }
        message && callback(new Error(message))
        callback()
    }

    private titleValidator(rule: any, value: string, callback: Function) {
        let message = ''
        const min = 2
        const max = 50
        const length = value.length
        if (!length) {
            message = '请输入标题'
        } else if (length < min) {
            message = '至少2个字符以上'
        } else if (length > max) {
            message = '名称在50个字符以内'
        }
        message && callback(new Error(message))
        callback()
    }

    private contentValidator(rule: any, value: string, callback: Function) {
        let message = ''
        const length = value.length
        if (!length) {
            message = '请输入内容'
        } else if (length < 10) {
            message = '请丰富一点内容吧'
        } else if (length > 20000) {
            message = '已超出字数范围'
        }
        message && callback(new Error(message))
        callback()
    }

    private onSelectChange() {
        this.isChange = true
        this.isInit = false
    }

    private onSelectRegion(value: any) {
        this.clue.region = value
    }

    private onContentChange(content: string) {
        if (content === this.clue.content) return
        this.isChange = true
        this.clue.content = content
        this.ruleForm.validateField('content')
    }

    private reset() {
        this.rawFileList = []
        ;(this.uploadRef as any).clearFiles()
        this.ruleForm.resetFields()
    }

    // 增加图片
    private onUploadChange(file: any) {
        this.isChange = true
        const raw = file.raw as any
        raw.key = +new Date()
        // 加入到源文件列表中（用于图片上传）
        this.rawFileList.push(raw)
        this.fileList.push(file)
        if (this.fileList.length >= this.imgLimit) {
            this.uploadEl.style.display = 'none'
        }
    }

    // 移除图片
    private onUploadRemove(file: any, fileList: any[]) {
        this.isChange = true
        if (this.UploadRemove) {
            this.UploadRemove(file, fileList, this)
            return
        }
        // 一开始就没有上传过图片的情况
        // 从 0 张图片开始
        this.rawFileList.splice(this.rawFileList.indexOf(file.raw), 1)
        this.fileList.splice(this.fileList.indexOf(file), 1)
        if (this.rawFileList.length < this.imgLimit) {
            this.uploadEl.style.display = 'inline-block'
        }
    }

    private onClearFiles() {
        this.uploadEl.style.display = 'inline-block'
        if (this.ClearFiles) {
            return this.ClearFiles(this)
        }
        // const size = this.rawFileList.length as number
        // (this.uploadRef as any).uploadFiles.splice(0, size)
        // this.rawFileList.splice(0, size)
        this.fileList = []
        this.clue.images = []
        this.rawFileList = []
    }

    /**
     * 附件下载：
     * - 真外链（非自家 CDN）：浏览器直连，不经后端代理
     * - 内部文件（S3 / 七牛 key / 七牛全链等形态）：走 /v1/annex/download（withApiHost 补全 API 域名），
     *   由后端解析归属（S3 拉流转 attachment / 七牛 302 + attname），文件名由后端决定
     */
    private downloadFile(link: string | null, name: string) {
        if (!link) {
            return
        }
        if (/^https?:\/\//i.test(link) && !link.includes('images.media.beer')) {
            window.open(link, '_blank')
            return
        }
        window.location.href = withApiHost('/v1/annex/download'
            + '?url=' + encodeURIComponent(link)
            + '&name=' + encodeURIComponent(name || '附件'))
    }

    private deleteFile(index: number) {
        this.$confirm('确定要删除文件吗？', '提示', {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
        }).then(() => {
            this.clue.annexes.splice(index, 1)
            this.isChange = true
            this.$message.success('文件删除成功')
        }).catch(() => {

        })
    }

    private validate(callback: Function) {
        const cb = (isValid: boolean) => {
            const isChange = this.checkChange()
            callback && callback(isValid, isChange)
        }
        this.ruleForm.validate(cb)
    }

    private checkChange() {
        // 标题需要单独检查
        if (this.isChange || this.clue.title !== this.value.title) {
            return true
        }
        return false
    }

    private getClue(type: string) {
        let data
        switch (type) {
            case 'clue_create':
            case 'clue_update':
                data = {
                    ...this.clue,
                    region: convertCodeToText(this.clue.region),
                    fileList: this.fileList
                }
                break
            case 'levy_create':
                data = {
                    title: this.clue.title,
                    content: this.clue.content,
                    images: this.clue.images
                }
                break
            case 'levy_update':
                break
            default:
                break
        }
        return data
    }

    private async uploadImg() {
        let data: any = []
        // 判断有无图片，进行上传
        if (this.rawFileList.length) {
            data = await this.uploadImages()
        }
        return data
    }

    private async uploadImages() {
        const mapPromise = (raw: File) => 
            new Promise(async (resolve, reject) => {
                try {
                    let result: any = await new UploadImg(raw).upload()
                    if (result.error) throw new Error('upload fail')
                    resolve(result.key)
                } catch (error) {
                    reject(error)
                }
            })
        const result = await Promise.all(this.rawFileList.map(mapPromise))
        return result
    }
}
